'use client';

import { useState, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import DataTable from '../../../components/DataTable';
import ActionMenu from '../../../components/ActionMenu';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';
import MasterFuelTypeAddEdit from './MasterFuelTypeAddEdit';
import { useFuelTypeList, deleteFuelType, revalidateFuelTypeList } from '../../../stores/fuelTypeStore';
import { fleetApi } from '../../../api/axiosClient';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const MasterFuelTypePage = () => {
  const { t } = useLanguage();
  const notify = useNotifier();

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [searchField, setSearchField] = useState('all');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  // LOCAL STATE FOR 'ALL' SEARCH
  const [localRows, setLocalRows] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'name', label: t('general.name', 'Name') },
      { value: 'code', label: t('masterUser.code', 'Code') },
      { value: 'category', label: t('general.category', 'Category') },
      { value: 'uom_type', label: t('general.uomType', 'UOM Type') },
      { value: 'description', label: t('general.description', 'Description') }
    ],
    [t]
  );

  // --- DEBOUNCE SEARCH INPUT ---
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(t);
  }, [searchRaw, searchField]);

  const [addEditOpen, setAddEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleSuccess = () => {
    revalidateFuelTypeList();
    setRefreshTrigger((prev) => prev + 1);
  };

  const hookSearchField = searchField === 'all' ? '' : searchField;
  const hookSearchValue = searchField === 'all' ? '' : searchValue;

  const {
    data: dataFromHook,
    totalRecord: hookTotal,
    isLoading: hookLoading
  } = useFuelTypeList(paginationModel.page, paginationModel.pageSize, hookSearchField, hookSearchValue);

  const rows = searchField === 'all' ? localRows : dataFromHook || [];
  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecords = searchField === 'all' ? localTotal : hookTotal || 0;

  // --- EFEK KHUSUS PENCARIAN 'ALL' ---
  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();

    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;

        let filters = [];
        if (searchValue) {
          filters = [
            { name: 'name', value: searchValue },
            { name: 'code', value: searchValue },
            { name: 'category', value: searchValue },
            { name: 'uom_type', value: searchValue },
            { name: 'description', value: searchValue }
          ];
        }

        const payload = {
          paging: { start, length },
          orders: { columns: ['id'], ascending: false },
          columns: [{ logic_operator: 'like', name: 'id', operator: 'AND', value: '' }],
          filters: filters
        };

        const resp = await fleetApi.post('/fuel-type/search' + getTzParam(), payload, { signal: controller.signal });
        const data = resp?.data ?? null;
        const fetchedRows = Array.isArray(data) ? data : (data?.data ?? []);
        const total = Array.isArray(data) ? fetchedRows.length : (data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? 0);

        setLocalRows(fetchedRows);
        setLocalTotal(total);
      } catch (err) {
        if (err?.name !== 'AbortError' && err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
          console.error('search all error', err);
        }
      } finally {
        if (!controller.signal.aborted) setLocalLoading(false);
      }
    };

    fetchAllSearch();
    return () => controller.abort();
  }, [searchField, searchValue, paginationModel, refreshTrigger]);

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecords > 0 ? totalRecords : 9999;
      const columns = [];

      if (searchField !== 'all' && searchValue) {
        columns.push({
          name: searchField,
          logic_operator: 'like',
          operator: 'AND',
          value: searchValue
        });
      }

      if (columns.length === 0) {
        columns.push({ logic_operator: 'like', name: 'id', operator: 'AND', value: '' });
      }

      let filters = [];
      if (searchField === 'all' && searchValue) {
        filters = [
          { name: 'name', value: searchValue },
          { name: 'code', value: searchValue },
          { name: 'category', value: searchValue },
          { name: 'uom_type', value: searchValue },
          { name: 'description', value: searchValue }
        ];
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['id'], ascending: false },
        columns: columns,
        filters: filters
      };

      const resp = await fleetApi.post('/fuel-type/search' + getTzParam(), payload);
      const data = resp?.data ?? null;
      return Array.isArray(data) ? data : (data?.data ?? []);
    } catch (err) {
      console.error('export all error', err);
      return [];
    }
  };

  const handleAdd = () => {
    setEditId(null);
    setAddEditOpen(true);
  };

  const handleEdit = (id) => {
    setEditId(id);
    setAddEditOpen(true);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete?.id) return;
    setIsDeleting(true);
    try {
      await deleteFuelType(itemToDelete.id);
      notify({ message: t('notify.delete', 'Deleted successfully'), severity: 'success' });
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      handleSuccess();
    } catch (err) {
      console.error('Failed to delete:', err);
      const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Failed to delete data';
      notify({ message: errorMessage, severity: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('general.name', 'Name'),
        flex: 1,
        minWidth: 150
      },
      {
        field: 'code',
        headerName: t('masterUser.code', 'Code'),
        flex: 1,
        minWidth: 100
      },
      {
        field: 'category',
        headerName: t('general.category', 'Category'),
        flex: 1,
        minWidth: 150
      },
      {
        field: 'uom_type',
        headerName: t('general.uomType', 'UOM Type'),
        flex: 1,
        minWidth: 130
      },
      {
        field: 'description',
        headerName: t('general.description', 'Description'),
        flex: 1,
        minWidth: 200
      },
      {
        field: 'id',
        headerName: 'Fuel Type ID',
        flex: 1,
        minWidth: 110,
        valueGetter: (value, row) => row.id ?? '-'
      },
      {
        field: 'action',
        headerName: t('buttonTitle.action', 'Action'),
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <ActionMenu mode="inline" row={params.row} onEdit={handleEdit} onDelete={() => handleDeleteClick(params.row)} />
        )
      }
    ],
    [t]
  );

  return (
    <Box>
      <DataTable
        title="Fuel Type List"
        columns={columns}
        rows={rows}
        loading={isLoading}
        pagination
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50]}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => {
          setSearchField(field || 'all');
          setSearchRaw(value ?? '');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        searchLabel="Search here"
        onAddClick={handleAdd}
        onFetchAllForExport={handleFetchAllForExport}
      />

      <MasterFuelTypeAddEdit open={addEditOpen} id={editId} onClose={() => setAddEditOpen(false)} onSuccess={handleSuccess} />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        title={t('general.deleteConfirmTitle', 'Delete Confirmation')}
        content={t('general.deleteConfirmMessage', 'Are you sure you want to delete') + ` "${itemToDelete?.name || ''}"?`}
      />
    </Box>
  );
};

export default MasterFuelTypePage;
